import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import AdminShell from '../components/admin/AdminShell';
import Icon from '../components/Icon';
import { SkeletonAdminBody } from '../components/Skeleton';
import { formatDateWithTime, formatMoney } from '../utils/format';

const formatLocalDateTimeValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const normalizePullItems = (payload) => {
  const candidates = [
    payload?.transactions,
    payload?.Transactions,
    payload?.transactionList,
    payload?.TransactionList,
    payload?.data,
    payload?.result,
    payload?.Result,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
};

const extractTransactionId = (row = {}) => {
  return String(
    row.TransactionID ||
    row.TransactionId ||
    row.transactionId ||
    row.transactionID ||
    row.ReceiptNo ||
    row.receiptNo ||
    row.receiptNumber ||
    ''
  ).trim().toUpperCase();
};

const extractAmount = (row = {}) => {
  const value = row.Amount ?? row.amount ?? row.TransAmount ?? row.transAmount ?? row.value ?? null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

function AdminMpesaPullPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [offsetValue, setOffsetValue] = useState('0');
  const [shortCode, setShortCode] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowLocal, setSelectedRowLocal] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [registerShortCode, setRegisterShortCode] = useState('');
  const [requestType, setRequestType] = useState('Pull');
  const [nominatedNumber, setNominatedNumber] = useState('');
  const [callbackUrl, setCallbackUrl] = useState('https://recordlogs.com/frp/mpesa/pulltransactions/result');

  useEffect(() => {
    let active = true;
    const loadUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await api.get('/auth/me');
        if (!active) return;
        const userData = response?.data || null;
        if (!userData?._id) {
          navigate('/login');
          return;
        }
        if (userData.role !== 'A') {
          navigate('/dashboard');
          return;
        }
        setCurrentUser(userData);
      } catch (_) {
        if (!active) return;
        localStorage.removeItem('token');
        navigate('/login');
      } finally {
        if (active) setLoadingUser(false);
      }
    };
    loadUser();
    return () => { active = false; };
  }, [navigate]);

  const pullTransactions = useCallback(async () => {
    if (!startDate || !endDate) {
      setError('Choose both start and end date.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');
      setResult(null);
      setSelectedRow(null);
      setSelectedRowLocal(null);
      const response = await api.post('/frp/admin/mpesa/pull-transactions', {
        shortCode: shortCode.trim() || undefined,
        startDate,
        endDate,
        offsetValue: offsetValue.trim() || '0',
      });
      const data = response?.data || null;
      const pullItems = normalizePullItems(data?.diagnostics?.responseBody || data?.query?.acknowledgementPayload || data?.responseBody || data);
      setResult({
        ...(data || {}),
        pullItems,
      });
      setMessage(data?.message || 'Pull query sent.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to pull transactions.');
    } finally {
      setLoading(false);
    }
  }, [endDate, offsetValue, shortCode, startDate]);

  const checkLocal = useCallback(async (row) => {
    const transactionId = extractTransactionId(row);
    if (!transactionId) {
      setError('This row has no transaction ID to compare.');
      return;
    }

    try {
      setError('');
      setSelectedRow(row);
      setSelectedRowLocal(null);
      const response = await api.get('/frp/admin/mpesa/reconciliation/search', {
        params: { transactionId }
      });
      setSelectedRowLocal(response?.data || null);
    } catch (err) {
      setSelectedRowLocal({
        error: err?.response?.data?.error || 'Failed to load local comparison.'
      });
    }
  }, []);

  const selectedTransactionId = useMemo(() => extractTransactionId(selectedRow || {}), [selectedRow]);
  const localFound = Boolean(selectedRowLocal?.reconciliation?.existsInApp || selectedRowLocal?.localMatches?.foundInApp);
  const canPreviewRecover = Boolean(selectedTransactionId && selectedRowLocal?.reconciliation?.existsInMpesa && !localFound);
  const selectedAmount = extractAmount(selectedRow || {});
  const selectedBillRef = String(
    selectedRow?.BillRefNumber ||
    selectedRow?.billRefNumber ||
    selectedRow?.AccountReference ||
    selectedRow?.accountReference ||
    selectedRow?.PartyB ||
    ''
  ).trim();

  const handleRecover = useCallback(async () => {
    if (!selectedTransactionId) return;
    if (!adminPassword.trim()) {
      setError('Enter your current PIN/password first.');
      return;
    }

    try {
      setRecovering(true);
      setError('');
      setMessage('');
      const response = await api.post('/frp/admin/mpesa/reconciliation/recover', {
        transactionId: selectedTransactionId,
        billRefNumber: selectedBillRef || undefined,
        amount: selectedAmount ?? undefined,
        adminPassword: adminPassword.trim(),
      });
      setMessage(response?.data?.message || 'Recovered successfully.');
      setSelectedRowLocal(response?.data || selectedRowLocal);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to recover the missing transaction.');
    } finally {
      setRecovering(false);
    }
  }, [adminPassword, selectedAmount, selectedBillRef, selectedRowLocal, selectedTransactionId]);

  const handleRegisterUrls = useCallback(async () => {
    try {
      setRegistering(true);
      setError('');
      setMessage('');
      const response = await api.post('/frp/admin/mpesa/pull-transactions/register', {
        shortCode: registerShortCode.trim() || undefined,
        requestType: requestType.trim(),
        nominatedNumber: nominatedNumber.trim(),
        callbackUrl: callbackUrl.trim(),
      });
      setMessage(response?.data?.message || 'Pull registration completed successfully.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to register pull API URL.');
    } finally {
      setRegistering(false);
    }
  }, [callbackUrl, nominatedNumber, registerShortCode, requestType]);

  if (loadingUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <SkeletonAdminBody rows={6} />
      </div>
    );
  }

  if (!currentUser) return null;

  const pullItems = Array.isArray(result?.pullItems) ? result.pullItems : [];

  return (
    <AdminShell user={currentUser} title="M-Pesa Pull" subtitle="Pull, compare, and preview recovery" maxWidth="max-w-7xl">
      <div className="space-y-5">
        <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Register C2B URLs</h2>
              <p className="text-sm text-slate-500">Quick form for the confirmation and validation URLs used by Safaricom.</p>
            </div>
            <button
              type="button"
              onClick={() => {
              setRegisterShortCode(String(currentUser?.businessShortCode || currentUser?.shortCode || '').trim());
                setRequestType('Pull');
                setNominatedNumber(String(currentUser?.phone || currentUser?.mobile || '').trim());
                setCallbackUrl('https://recordlogs.com/frp/mpesa/pulltransactions/result');
              }}
              className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"
            >
              Autofill
            </button>
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-4">
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Short code</span>
              <input value={registerShortCode} onChange={(e) => setRegisterShortCode(e.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm" placeholder="Business shortcode" />
            </label>
            <label className="block lg:col-span-1">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Request type</span>
              <select value={requestType} onChange={(e) => setRequestType(e.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm">
                <option value="Pull">Pull</option>
              </select>
            </label>
            <label className="block lg:col-span-2">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Nominated number</span>
              <input value={nominatedNumber} onChange={(e) => setNominatedNumber(e.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm" placeholder="Phone number or agent number" />
            </label>
            <label className="block lg:col-span-2">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Callback URL</span>
              <input value={callbackUrl} onChange={(e) => setCallbackUrl(e.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm" />
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleRegisterUrls}
              disabled={registering}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white"
            >
              <Icon name="shield-check" size="1rem" />
              {registering ? 'Registering...' : 'Register URLs'}
            </button>
          </div>
        </div>

        <div className="rounded-3xl border border-emerald-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="grid gap-4 lg:grid-cols-4">
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Short code</span>
              <input value={shortCode} onChange={(e) => setShortCode(e.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm" placeholder="Business shortcode" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Start date</span>
              <input type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">End date</span>
              <input type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Offset</span>
              <input value={offsetValue} onChange={(e) => setOffsetValue(e.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm" />
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button onClick={pullTransactions} disabled={loading} className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white">
              <Icon name="arrow-repeat" size="1rem" />
              {loading ? 'Pulling...' : 'Pull transactions'}
            </button>
          </div>
          {message ? <div className="mt-3 text-sm text-emerald-700">{message}</div> : null}
          {error ? <div className="mt-3 text-sm text-rose-700">{error}</div> : null}
        </div>

        <div className="grid gap-5 lg:grid-cols-[1.4fr_0.9fr]">
          <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Pulled transactions</h2>
                <p className="text-sm text-slate-500">Compare each row with local records before recovery.</p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{pullItems.length} rows</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[11px] uppercase tracking-[0.16em] text-slate-500">
                    <th className="py-3 pr-3">Transaction</th>
                    <th className="py-3 pr-3">Amount</th>
                    <th className="py-3 pr-3">Date</th>
                    <th className="py-3 pr-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pullItems.map((row, index) => {
                    const transactionId = extractTransactionId(row) || `ROW-${index + 1}`;
                    const amount = extractAmount(row);
                    const rowDate = row.TransactionDate || row.transactionDate || row.Date || row.date || row.CreatedDate || row.createdAt || '';
                    return (
                      <tr key={`${transactionId}-${index}`} className="border-b border-slate-100 align-top">
                        <td className="py-3 pr-3 font-medium text-slate-900">{transactionId}</td>
                        <td className="py-3 pr-3">{amount != null ? `Ksh ${formatMoney(amount)}` : '-'}</td>
                        <td className="py-3 pr-3">{rowDate ? formatDateWithTime(rowDate) : '-'}</td>
                        <td className="py-3 pr-3">
                          <button
                            type="button"
                            onClick={() => checkLocal(row)}
                            className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"
                          >
                            Compare
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!pullItems.length ? (
                    <tr><td className="py-6 text-sm text-slate-500" colSpan={4}>No pulled transactions yet.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-5">
            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <h2 className="text-base font-semibold text-slate-900">Preview</h2>
              <div className="mt-3 space-y-2 text-sm text-slate-700">
                <div><span className="text-slate-500">Transaction:</span> {selectedTransactionId || '-'}</div>
                <div><span className="text-slate-500">Amount:</span> {selectedAmount != null ? `Ksh ${formatMoney(selectedAmount)}` : '-'}</div>
                <div><span className="text-slate-500">Account ref:</span> {selectedBillRef || '-'}</div>
                <div><span className="text-slate-500">On M-Pesa:</span> {selectedRowLocal?.reconciliation?.existsInMpesa ? 'Found' : 'Not checked'}</div>
                <div><span className="text-slate-500">In app:</span> {localFound ? 'Found' : 'Missing'}</div>
              </div>
            </div>

            {canPreviewRecover ? (
              <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 shadow-sm sm:p-5">
                <h2 className="text-base font-semibold text-amber-900">Recovery preview</h2>
                <p className="mt-2 text-sm text-amber-800">
                  This transaction is confirmed on M-Pesa and is missing locally. Enter your PIN to recover it into the matching wallet only after you review the preview.
                </p>
                <label className="mt-4 block">
                  <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-900">Current PIN</span>
                  <input type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} className="w-full rounded-2xl border border-amber-300 px-4 py-3 text-sm" />
                </label>
                <button
                  type="button"
                  onClick={handleRecover}
                  disabled={recovering}
                  className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-amber-500 px-5 py-3 text-sm font-semibold text-white"
                >
                  <Icon name="shield-check" size="1rem" />
                  {recovering ? 'Recovering...' : 'Recover this transaction'}
                </button>
              </div>
            ) : (
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 shadow-sm">
                Pull a transaction, click <span className="font-semibold">Compare</span>, and the preview will show whether it already exists in the app.
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

export default AdminMpesaPullPage;
